'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { getImageUrl } from '@/lib/utils'
import { IMAGE_PRESETS } from '@/lib/image-presets'
import { Calendar, Newspaper } from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import { SonDakikaRowsSkeleton } from '@/components/PageSkeletons'
import { SITE_CONTAINER, SITE_MAIN_PAD_Y } from '@/lib/site-layout'
import { getApiBaseUrl } from '@/lib/config'
import { formatSiteDate, formatSiteTime } from '@/lib/site-datetime'

interface News {
  id: number
  title: string
  slug: string
  image: string
  titleSub?: string
  createdAt: string
  category: {
    id: number
    title: string
    slug: string
    color?: string
  }
}

function mergeUniqueNews(prev: News[], incoming: News[]): News[] {
  if (incoming.length === 0) return prev
  const seen = new Set(prev.map((item) => item.id))
  const merged = [...prev]
  for (const item of incoming) {
    if (seen.has(item.id)) continue
    seen.add(item.id)
    merged.push(item)
  }
  return merged
}

function SonDakikaListSkeleton() {
  return <SonDakikaRowsSkeleton rows={5} />
}

export default function SonDakikaPage() {
  const [news, setNews] = useState<News[]>([])
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [countdown, setCountdown] = useState(60)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const fetchInFlightRef = useRef(false)

  const fetchNews = useCallback(async (pageNum: number, append: boolean = false) => {
    if (pageNum === 1) setLoading(true)
    else setLoadingMore(true)

    try {
      const apiUrl = getApiBaseUrl()
      const res = await fetch(`${apiUrl}/api/news/latest?limit=20&page=${pageNum}`)
      if (!res.ok) throw new Error('Failed to fetch')
      const data = await res.json()
      const newNews = data.data || data.news || []

      if (newNews.length < 20) {
        setHasMore(false)
      } else if (!append) {
        setHasMore(true)
      }

      if (append) {
        setNews((prev) => mergeUniqueNews(prev, newNews))
      } else {
        setNews(mergeUniqueNews([], newNews))
        setPage(1)
      }
    } catch (error) {
      console.error('Error fetching news:', error)
    } finally {
      fetchInFlightRef.current = false
      setLoading(false)
      setLoadingMore(false)
    }
  }, [])

  useEffect(() => {
    fetchInFlightRef.current = true
    void fetchNews(1)
  }, [fetchNews])

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (!fetchInFlightRef.current) {
            fetchInFlightRef.current = true
            void fetchNews(1)
          }
          return 60
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [fetchNews])

  useEffect(() => {
    if (loading) return

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting || !hasMore || fetchInFlightRef.current) return

        fetchInFlightRef.current = true
        setPage((prev) => {
          const nextPage = prev + 1
          void fetchNews(nextPage, true)
          return nextPage
        })
      },
      { threshold: 0.1 },
    )

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [loading, hasMore, loadingMore, fetchNews])

  const groupedNews: Record<string, News[]> = {}
  news.forEach((item) => {
    const date = formatSiteDate(item.createdAt, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    if (!groupedNews[date]) {
      groupedNews[date] = []
    }
    groupedNews[date].push(item)
  })

  return (
    <div className="min-h-screen min-w-0 max-w-full bg-gray-50 dark:bg-slate-950">
      <div className={`${SITE_CONTAINER} ${SITE_MAIN_PAD_Y}`}>
        <PageHeader
          title="Son Dakika"
          description="Güncel gelişmeler ve flaş haberler"
          action={
            <div className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 dark:border-slate-700 dark:bg-slate-900 dark:text-gray-300 sm:px-3 sm:py-1.5 sm:text-sm">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-green-500" aria-hidden />
              <span className="tabular-nums">{countdown}s</span>
              {news.length > 0 ? (
                <span className="hidden text-gray-400 sm:inline">· {news.length}</span>
              ) : null}
            </div>
          }
        />

        {loading ? (
          <div className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <SonDakikaListSkeleton />
          </div>
        ) : (
          <div className="space-y-5 sm:space-y-6">
            {Object.entries(groupedNews).map(([date, items]) => (
              <section key={date}>
                <div className="mb-2 flex items-center gap-2 sm:mb-3">
                  <div className="h-px flex-1 bg-gray-200 dark:bg-slate-800" />
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 sm:text-sm">
                    <Calendar className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {date}
                  </span>
                  <div className="h-px flex-1 bg-gray-200 dark:bg-slate-800" />
                </div>

                <div className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  {items.map((item, index) => {
                    const time = formatSiteTime(item.createdAt, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })

                    return (
                      <article
                        key={item.id}
                        className={
                          index !== items.length - 1
                            ? 'border-b border-gray-100 dark:border-slate-800'
                            : ''
                        }
                      >
                        <Link
                          href={`/${item.category?.slug || 'haber'}/${item.slug}-${item.id}`}
                          className="group flex min-w-0 gap-3 p-3 transition-colors hover:bg-gray-50 dark:hover:bg-slate-800/50 sm:gap-4 sm:p-4"
                        >
                          {/* Desktop timeline time */}
                          <div className="hidden w-14 shrink-0 flex-col items-center pt-1 sm:flex">
                            <span className="mb-1 h-2 w-2 rounded-full bg-[#D92E2E] transition-transform group-hover:scale-125" />
                            <time className="text-base font-bold tabular-nums leading-none text-[#D92E2E]">
                              {time}
                            </time>
                          </div>

                          <div className="relative h-[4.25rem] w-24 shrink-0 overflow-hidden rounded-md bg-gray-100 dark:bg-slate-800 sm:h-20 sm:w-32 sm:rounded-lg md:h-24 md:w-40">
                            {getImageUrl(item.image, IMAGE_PRESETS.breakingThumb) ? (
                              <Image
                                src={getImageUrl(item.image, IMAGE_PRESETS.breakingThumb)}
                                alt={item.title}
                                fill
                                className="object-cover transition-transform duration-500 group-hover:scale-105"
                                loading="lazy"
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800">
                                <svg
                                  className="h-6 w-6 text-gray-400 dark:text-slate-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>

                          <div className="flex min-w-0 flex-1 flex-col justify-center">
                            <h2 className="line-clamp-2 text-sm font-bold leading-snug text-gray-900 transition-colors group-hover:text-[#D92E2E] dark:text-white sm:text-base">
                              {item.title}
                            </h2>
                            {item.titleSub ? (
                              <p className="mt-0.5 hidden line-clamp-1 text-sm text-gray-500 dark:text-gray-400 md:block">
                                {item.titleSub}
                              </p>
                            ) : null}
                            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400 sm:text-xs">
                              <time className="font-semibold tabular-nums text-[#D92E2E] sm:hidden">
                                {time}
                              </time>
                              {item.category ? (
                                <>
                                  <span className="sm:hidden" aria-hidden>
                                    ·
                                  </span>
                                  <span
                                    className="font-semibold"
                                    style={{ color: item.category.color || '#D92E2E' }}
                                  >
                                    {item.category.title}
                                  </span>
                                </>
                              ) : null}
                            </div>
                          </div>

                          <div className="hidden items-center md:flex">
                            <svg
                              className="h-5 w-5 text-gray-300 transition-all group-hover:translate-x-1 group-hover:text-[#D92E2E] dark:text-gray-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </Link>
                      </article>
                    )
                  })}
                </div>
              </section>
            ))}

            <div ref={loadMoreRef} className="py-2 sm:py-3">
              {loadingMore && (
                <div className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <SonDakikaListSkeleton />
                </div>
              )}
              {!hasMore && news.length > 0 && (
                <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  <Newspaper className="mx-auto mb-2 h-8 w-8 opacity-50" aria-hidden />
                  <p>Tüm haberler yüklendi</p>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && news.length === 0 && (
          <div className="rounded-lg border border-gray-100 bg-white px-4 py-10 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:py-12">
            <Newspaper className="mx-auto mb-3 h-12 w-12 text-gray-300 dark:text-gray-600" aria-hidden />
            <h3 className="mb-1 text-lg font-bold text-gray-900 dark:text-white">Henüz haber yok</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Son dakika haberleri burada listelenecek
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
